import { StyleDeclarationValue, css } from 'aphrodite';
import { observer } from 'mobx-react';
import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { canViewTexting } from '../../../extViewmodels/Utils';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { usePhoneNumberUserQuery } from '../../../queries';
import { KnownCategories } from '../../../viewmodels/AppViewModels';
import {
	AppDrawer,
	AppDrawerToolbar,
	AppDrawerToolbarButtonAiAssistant,
	StickyAppDrawerContent,
} from '../../components-v2/Drawer/AppDrawer';
import { Page } from '../../components-v2/PageLayout';
import { MultiContainerHeader } from '../../components/MultiContainerHeader';
import { BlogIcon } from '../../components/svgs/icons/BlogIcon';
import { EmailIcon } from '../../components/svgs/icons/EmailIcon';
import { HeartAndShareIcon } from '../../components/svgs/icons/HeartAndShareIcon';
import { ImageIcon } from '../../components/svgs/icons/ImageIcon';
import { PaperPlaneIcon } from '../../components/svgs/icons/PaperPlaneIcon';
import { PostageIcon } from '../../components/svgs/icons/PostageIcon';
import { TextingIcon } from '../../components/svgs/icons/TextingIcon';
import { IconTabTrigger, TabContent, TabList, TabRoot } from '../../components-v2/Tabs';
import { baseStyleSheet } from '../../styles/styles';
import { useCampaignParams } from '../CampaignBrowser';
import type { INavigationItemProps } from '../MainContainer';
import { DefaultUniversalAssistantChat } from '../templates/AssistantChat';
import { BlogsCampaignCards } from './BlogsCampaignCards';
import { EmailCampaignCards } from './EmailCampaignCards';
import { HandwrittenCardsCampaignCards } from './HandwrittenCardsCampaignCards';
import { HtmlNewslettersCampaignCards } from './HtmlNewslettersCampaignCards';
import { ImagesTabPage } from './ImagesTabPage';
import { SocialMediaCampaignCards } from './SocialMedia/SocialMediaCampaignCards';
import { styleSheet } from './styles';
import { TextingTabPage } from './texting/TextingTabPage';

interface IProps extends INavigationItemProps {
	className?: string;
	styles?: StyleDeclarationValue[];
}

export type TabValues = 'email' | 'social' | 'cards' | 'texting' | 'blogs' | 'newsletters' | 'images';

export const Campaigns: FC<IProps> = observer(({ className, styles }) => {
	const userSession = useUserSession();
	const params = useParams<{ tab?: string }>();
	const history = useHistory();
	const { setCampaignBrowserParam } = useCampaignParams();
	const phoneNumberUserQuery = usePhoneNumberUserQuery();
	const socialEnabled =
		userSession?.account?.features?.socialMedia?.enabled &&
		userSession.user?.userGroupPermissions?.featurePermissions?.socialMedia?.send;
	const cardsEnabled =
		userSession?.account?.features?.handwrittenCards?.enabled &&
		userSession.user?.userGroupPermissions?.featurePermissions?.handwrittenCard?.send;
	const blogsEnabled: boolean = userSession?.account?.features?.blogFeature?.enabled;
	const textingEnabled: boolean = canViewTexting(userSession) && Boolean(phoneNumberUserQuery?.data?.length);
	const htmlNewslettersEnabled: boolean = userSession?.account?.features?.htmlNewsletter?.enabled;

	const [TabListAccessory, setTabListAccessory] = useState<ReactNode>();

	const selectedTab: TabValues = useMemo((): TabValues => {
		return (params.tab?.toLowerCase() as TabValues) ?? 'email';
	}, [params.tab]);

	const isValidTab = useCallback((tab: string): boolean => {
		return ['email', 'social', 'cards', 'texting', 'blogs', 'newsletters', 'images'].includes(tab?.toLowerCase());
	}, []);

	const redirectToEmail = useCallback(() => {
		history.replace({ pathname: `/campaigns/email` });
	}, [history]);

	useEffect(() => {
		if (!isValidTab(params.tab)) {
			redirectToEmail();
		}
	}, [params.tab, redirectToEmail, isValidTab]);

	const onSelectedTabChanged = (tabValue: TabValues) => {
		const navParamsObj = { tabUrlParam: tabValue.toLocaleLowerCase() };
		switch (tabValue) {
			case 'social':
			case 'email':
			case 'texting':
				setCampaignBrowserParam({ ...navParamsObj });
				break;
			case 'blogs':
			case 'cards':
				setCampaignBrowserParam({
					category: KnownCategories.All,
					...navParamsObj,
				});
				break;
			case 'newsletters':
			case 'images':
				setCampaignBrowserParam({ ...navParamsObj });
				break;
			default:
				throw new Error('Invalid tab selected');
		}
	};

	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} ${className || ''}`}>
			<MultiContainerHeader
				appBarContentStyles={[styleSheet.appBarContent]}
				appBarHeader={<h1 className={css(baseStyleSheet.breadcrumbTitle)}>Campaigns</h1>}
				appBarStyles={[styleSheet.appBar]}
			/>
			<Page>
				<TabRoot value={selectedTab} onValueChange={tab => onSelectedTabChanged(tab as TabValues)}>
					<TabList>
						<IconTabTrigger icon={<EmailIcon fillColor='currentColor' />} value='email'>
							Email
						</IconTabTrigger>
						{socialEnabled && (
							<IconTabTrigger icon={<HeartAndShareIcon fillColor='currentColor' />} value='social'>
								Social
							</IconTabTrigger>
						)}
						{cardsEnabled && (
							<IconTabTrigger icon={<PostageIcon fillColor='currentColor' />} value='cards'>
								Cards
							</IconTabTrigger>
						)}
						{textingEnabled && (
							<IconTabTrigger icon={<TextingIcon fill='currentColor' />} value='texting'>
								Texting
							</IconTabTrigger>
						)}
						{blogsEnabled && (
							<IconTabTrigger icon={<BlogIcon fill='currentColor' />} value='blogs'>
								Blogs
							</IconTabTrigger>
						)}
						{htmlNewslettersEnabled && (
							<IconTabTrigger icon={<PaperPlaneIcon />} value='newsletters'>
								HTML Newsletters
							</IconTabTrigger>
						)}
						<IconTabTrigger icon={<ImageIcon />} value='images'>
							My Images
						</IconTabTrigger>
						{!!TabListAccessory && (
							<div className={css(styleSheet.accessoryContainer, styleSheet.rowLayout)}>{TabListAccessory}</div>
						)}
					</TabList>

					<TabContent value='email'>
						<EmailCampaignCards />
					</TabContent>

					{socialEnabled && (
						<TabContent value='social'>
							<SocialMediaCampaignCards />
						</TabContent>
					)}
					{cardsEnabled && (
						<TabContent value='cards'>
							<HandwrittenCardsCampaignCards setTabListAccessory={setTabListAccessory} />
						</TabContent>
					)}
					{textingEnabled && (
						<TabContent value='texting'>
							<TextingTabPage setTabListAccessory={setTabListAccessory} />
						</TabContent>
					)}
					{blogsEnabled && (
						<TabContent value='blogs'>
							<BlogsCampaignCards />
						</TabContent>
					)}
					{htmlNewslettersEnabled && (
						<TabContent value='newsletters'>
							<HtmlNewslettersCampaignCards />
						</TabContent>
					)}
					<TabContent value='images'>
						<ImagesTabPage />
					</TabContent>
				</TabRoot>
			</Page>
			<AppDrawer>
				<StickyAppDrawerContent value='ai-assistant'>
					<DefaultUniversalAssistantChat />
				</StickyAppDrawerContent>
				<AppDrawerToolbar>
					<AppDrawerToolbarButtonAiAssistant value='ai-assistant' />
				</AppDrawerToolbar>
			</AppDrawer>
		</div>
	);
});
